import React, { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { safeSetState } from '../utils/serializationUtils'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

// Helper to extract ONLY safe primitive data from user
function extractUserData(user) {
  if (!user) return null
  
  // Extract ONLY primitive fields
  return {
    id: String(user.id || ''),
    email: String(user.email || ''),
    role: String(user.role || 'student'),
  }
}

// Helper to extract ONLY safe primitive data from session
function extractSessionData(session) {
  if (!session) return null
  
  // Extract ONLY primitive fields
  return {
    access_token: String(session.access_token || ''),
    refresh_token: String(session.refresh_token || ''),
    expires_at: Number(session.expires_at || 0),
    token_type: String(session.token_type || 'bearer'),
  }
}

export const AuthProvider = ({ children }) => {
  // Initialize with explicit null
  const [user, setUserRaw] = useState(null)
  const [session, setSessionRaw] = useState(null)
  const [loading, setLoadingRaw] = useState(true)
  const [authError, setAuthError] = useState(null)
  
  // SEPARATED ACCESS STATES - one per product type
  const [hasTestAccess, setHasTestAccessRaw] = useState(false)  // Regular test program
  const [hasSchoolFGAccess, setHasSchoolFGAccessRaw] = useState(false)  // School Financial Literacy
  const [schoolInfo, setSchoolInfoRaw] = useState(null)  // School metadata
  const [accessLoading, setAccessLoadingRaw] = useState(false)
  
  // Legacy compatibility: hasPaidAccess maps to test access ONLY
  const hasPaidAccess = hasTestAccess
  
  
  // ADMIN ROLE STATE
  const [isAdmin, setIsAdminRaw] = useState(false)
  const [adminChecked, setAdminCheckedRaw] = useState(false)
  // Phase 3 — owner / super-admin flag (env-controlled via backend)
  const [isOwner, setIsOwnerRaw] = useState(false)

  // Safe setters
  const setUser = (value) => safeSetState(setUserRaw, value, 'user')
  const setSession = (value) => safeSetState(setSessionRaw, value, 'session')
  const setLoading = (value) => safeSetState(setLoadingRaw, value, 'loading')
  const setHasTestAccess = (value) => safeSetState(setHasTestAccessRaw, Boolean(value), 'hasTestAccess')
  const setHasSchoolFGAccess = (value) => safeSetState(setHasSchoolFGAccessRaw, Boolean(value), 'hasSchoolFGAccess')
  const setSchoolInfo = (value) => safeSetState(setSchoolInfoRaw, value, 'schoolInfo')
  const setAccessLoading = (value) => safeSetState(setAccessLoadingRaw, Boolean(value), 'accessLoading')
  const setIsAdmin = (value) => safeSetState(setIsAdminRaw, Boolean(value), 'isAdmin')
  const setAdminChecked = (value) => safeSetState(setAdminCheckedRaw, Boolean(value), 'adminChecked')
  const setIsOwner = (value) => safeSetState(setIsOwnerRaw, Boolean(value), 'isOwner')

  // Emergency auth cleanup - clears ALL auth state including Supabase localStorage
  const emergencyAuthCleanup = async () => {
    console.warn('[Auth] 🚨 EMERGENCY CLEANUP - Clearing all auth state')
    
    try {
      // Clear React state
      setUser(null)
      setSession(null)
      setAuthError(null)
      
      // Force clear Supabase session from localStorage
      if (supabase) {
        try {
          await supabase.auth.signOut({ scope: 'local' })
        } catch (e) {
          console.warn('[Auth] Emergency signOut failed (continuing):', e)
        }
      }
      
      // Extra safety: manually clear any auth-related localStorage keys
      try {
        const keysToRemove = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key && (key.includes('supabase') || key.includes('auth'))) {
            keysToRemove.push(key)
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key))
      } catch (e) {
        console.warn('[Auth] localStorage cleanup failed:', e)
      }
      
      console.log('[Auth] ✅ Emergency cleanup complete')
      return true
    } catch (error) {
      console.error('[Auth] Emergency cleanup error:', error)
      return false
    }
  }

  // Validate if session is actually usable
  const validateSession = (sessionData) => {
    if (!sessionData) return { valid: false, reason: 'no_session' }
    
    // Check if session has required fields
    if (!sessionData.access_token) {
      return { valid: false, reason: 'missing_access_token' }
    }
    
    if (!sessionData.user || !sessionData.user.id) {
      return { valid: false, reason: 'missing_user' }
    }
    
    // Check if session is expired
    if (sessionData.expires_at) {
      const expiresAt = sessionData.expires_at * 1000 // Convert to milliseconds
      const now = Date.now()
      
      if (expiresAt < now) {
        return { valid: false, reason: 'expired' }
      }
      
      // Warn if expiring soon (within 5 minutes)
      if (expiresAt - now < 5 * 60 * 1000) {
        console.warn('[Auth] ⚠️ Session expiring soon')
      }
    }
    
    return { valid: true }
  }

  useEffect(() => {
    let mounted = true

    // Initialize auth with validation
    const initAuth = async () => {
      try {
        if (!supabase) {
          console.error('[Auth] Supabase client not initialized')
          if (mounted) {
            setLoading(false)
            setAuthError('supabase_not_initialized')
          }
          return
        }

        console.log('[Auth] 🔐 Initializing auth...')

        // Get session from Supabase (will restore from localStorage if exists)
        const result = await supabase.auth.getSession()
        
        if (!mounted) return

        // Check for error
        if (result.error) {
          console.error('[Auth] ❌ Session restore error:', result.error?.message || String(result.error))
          
          // Session restore failed - clean up and start fresh
          await emergencyAuthCleanup()
          
          if (mounted) {
            setSession(null)
            setUser(null)
            setLoading(false)
            setAuthError('session_restore_failed')
          }
          return
        }

        // Extract session data
        const sessionData = result.data?.session
        
        if (sessionData) {
          // CRITICAL: Validate restored session before using it
          const validation = validateSession(sessionData)
          
          if (!validation.valid) {
            console.warn(`[Auth] ⚠️ Invalid restored session: ${validation.reason}`)
            console.log('[Auth] Clearing invalid session...')
            
            // Invalid session - clean up
            await emergencyAuthCleanup()
            
            if (mounted) {
              setSession(null)
              setUser(null)
              setLoading(false)
              setAuthError(`invalid_session_${validation.reason}`)
            }
            return
          }
          
          // Session is valid - extract safe data
          const safeSession = extractSessionData(sessionData)
          const safeUser = extractUserData(sessionData.user)
          
          console.log('[Auth] ✅ Valid session restored - User:', safeUser.email)
          
          if (mounted) {
            setSession(safeSession)
            setUser(safeUser)
            setAuthError(null)
          }
        } else {
          // No session - clean state
          console.log('[Auth] No session found (guest user)')
          if (mounted) {
            setSession(null)
            setUser(null)
            setAuthError(null)
          }
        }
        
        if (mounted) setLoading(false)
        
      } catch (error) {
        console.error('[Auth] ❌ Init error:', error?.message || String(error))
        
        // Init failed - emergency cleanup
        await emergencyAuthCleanup()
        
        if (mounted) {
          setSession(null)
          setUser(null)
          setLoading(false)
          setAuthError('init_failed')
        }
      }
    }

    initAuth()

    // Auth state listener with validation
    let subscription = null
    if (supabase) {
      try {
        const { data } = supabase.auth.onAuthStateChange(
          async (event, sessionData) => {
            if (!mounted) return

            console.log('[Auth] 🔔 State change:', event)

            try {
              // Validate incoming session data
              if (sessionData) {
                const validation = validateSession(sessionData)
                
                if (!validation.valid) {
                  console.warn(`[Auth] ⚠️ Invalid session in state change (${event}): ${validation.reason}`)
                  
                  // Don't accept invalid session
                  await emergencyAuthCleanup()
                  
                  if (mounted) {
                    setSession(null)
                    setUser(null)
                    setAuthError(`invalid_session_${validation.reason}`)
                  }
                  return
                }
                
                // Session is valid - extract safe data
                const safeSession = extractSessionData(sessionData)
                const safeUser = extractUserData(sessionData.user)
                
                if (mounted) {
                  setSession(safeSession)
                  setUser(safeUser)
                  setAuthError(null)
                }
              } else {
                // Session cleared
                if (mounted) {
                  setSession(null)
                  setUser(null)
                  setAuthError(null)
                }
              }
            } catch (error) {
              console.error('[Auth] ❌ State change error:', error?.message || String(error))
              
              // State change handler failed - emergency cleanup
              await emergencyAuthCleanup()
              
              if (mounted) {
                setSession(null)
                setUser(null)
                setAuthError('state_change_failed')
              }
            }

            if (mounted) setLoading(false)
          }
        )

        subscription = data.subscription
      } catch (error) {
        console.error('[Auth] ❌ Subscription error:', error?.message || String(error))
      }
    }

    return () => {
      mounted = false
      if (subscription) {
        try {
          subscription.unsubscribe()
        } catch (e) {
          console.error('[Auth] Cleanup error:', e?.message || String(e))
        }
      }
    }
  }, [])

  const signUp = async (email, password, displayName) => {
    try {
      if (!supabase) {
        throw new Error('Supabase nie je inicializovaný')
      }

      // Make request
      const result = await supabase.auth.signUp({ email, password })

      // Check for error - use only error code/status to avoid body stream issues
      if (result.error) {
        // Map common Supabase auth errors to user-friendly Slovak messages
        const errorCode = result.error.code || result.error.status
        const errorMessage = result.error.message || ''
        
        // Check for user already exists (various Supabase error formats)
        if (errorCode === 'user_already_exists' || 
            errorCode === '422' || 
            errorMessage.toLowerCase().includes('already') ||
            errorMessage.toLowerCase().includes('exists') ||
            errorMessage.toLowerCase().includes('registered')) {
          throw new Error('Účet s týmto emailom už existuje. Skúste sa prihlásiť.')
        }
        
        // Other common auth error codes
        if (errorCode === 'email_exists') {
          throw new Error('Tento email sa už používa.')
        }
        if (errorCode === 'weak_password' || errorMessage.toLowerCase().includes('password')) {
          throw new Error('Heslo musí mať aspoň 6 znakov.')
        }
        if (errorCode === 'invalid_credentials') {
          throw new Error('Neplatné prihlasovacie údaje.')
        }
        
        // Default error message
        throw new Error('Registrácia zlyhala. Skúste to prosím znova.')
      }

      // Extract user ID immediately
      const userId = result.data?.user?.id
      const userEmail = result.data?.user?.email

      if (!userId) {
        throw new Error('Nepodarilo sa vytvoriť užívateľský účet')
      }

      // Frontend fallback: Create profile if trigger didn't work.
      // NOTE: We deliberately do NOT name `role` here. The DB has
      // `role TEXT DEFAULT 'student'`, and the isolated project further
      // REVOKEs INSERT on the `role` column from anon/authenticated so
      // any client-supplied role would cause Postgres to reject the
      // whole statement. Omitting it lets the DEFAULT take effect and
      // works uniformly against production and isolated environments.
      try {
        const { error: profileError } = await supabase.from('user_profiles').insert([{
          id: userId,
          email: email,
          display_name: displayName || email.split('@')[0]
        }])
        
        // Ignore conflict errors (profile already created by trigger)
        if (profileError && !profileError.message?.includes('duplicate') && profileError.code !== '23505') {
          console.warn('[Auth] Profile creation warning:', profileError.message)
        }
      } catch (profileError) {
        console.warn('[Auth] Profile creation fallback error:', profileError?.message || String(profileError))
      }

      // Return only plain primitives
      return {
        success: true,
        userId: String(userId),
        email: String(userEmail || email),
      }
    } catch (error) {
      // Re-throw with the message (already user-friendly from above)
      throw new Error(error.message || 'Registrácia zlyhala')
    }
  }

  const signIn = async (email, password) => {
    try {
      if (!supabase) {
        throw new Error('Supabase nie je inicializovaný')
      }

      // Make request
      const result = await supabase.auth.signInWithPassword({ email, password })

      // Check for error - use only error code/status to avoid body stream issues
      if (result.error) {
        // Map common Supabase auth errors to user-friendly Slovak messages
        const errorCode = result.error.code || result.error.status
        
        // Common auth error codes
        if (errorCode === 'invalid_credentials' || errorCode === 'invalid_grant') {
          throw new Error('Nesprávny email alebo heslo.')
        }
        if (errorCode === 'email_not_confirmed') {
          const err = new Error('Email nie je potvrdený. Skontrolujte si emailovú schránku.')
          // Preserve the code so the UI can offer a "resend verification email" action.
          err.code = 'email_not_confirmed'
          throw err
        }
        if (errorCode === 'user_not_found') {
          throw new Error('Účet s týmto emailom neexistuje.')
        }
        if (errorCode === 'too_many_requests' || result.error.message?.includes('rate limit')) {
          throw new Error('Príliš veľa pokusov. Skúste to prosím neskôr.')
        }
        
        // Default error message
        throw new Error('Nesprávny email alebo heslo.')
      }

      // Extract primitives immediately
      const userId = result.data?.user?.id
      const userEmail = result.data?.user?.email

      if (!userId) {
        throw new Error('Neplatné prihlasovacie údaje')
      }

      // Frontend fallback: Ensure profile exists after login
      try {
        const { data: profile, error: profileCheckError } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('id', userId)
          .single()

        // If profile doesn't exist, create it.
        // See registration path above — we omit `role` so the DB DEFAULT
        // 'student' is applied and no client-side privilege on the `role`
        // column is required.
        if (profileCheckError || !profile) {
          console.log('[Auth] Profile missing, creating fallback profile...')
          await supabase.from('user_profiles').insert([{
            id: userId,
            email: userEmail || email,
            display_name: (userEmail || email).split('@')[0]
          }])
        }
      } catch (profileError) {
        // Don't fail login if profile check/creation fails
        console.warn('[Auth] Profile fallback error:', profileError?.message || String(profileError))
      }

      // Return only plain primitives
      return {
        success: true,
        userId: String(userId),
        email: String(userEmail || email),
      }
    } catch (error) {
      // Re-throw with the message (already user-friendly from above)
      const wrapped = new Error(error.message || 'Prihlásenie zlyhalo')
      // Preserve custom codes (e.g. 'email_not_confirmed') so the UI can
      // offer inline actions like "resend verification email".
      if (error.code) wrapped.code = error.code
      throw wrapped
    }
  }

  const resendVerification = async (email) => {
    if (!supabase) {
      throw new Error('Overovací e-mail sa nepodarilo odoslať. Skúste to neskôr alebo nás kontaktujte na podpora@edumisia.sk.')
    }
    const cleanEmail = (email || '').trim()
    if (!cleanEmail) {
      throw new Error('Zadajte prosím e-mail, na ktorý ste sa registrovali.')
    }
    try {
      const result = await supabase.auth.resend({ type: 'signup', email: cleanEmail })
      if (result?.error) {
        const code = result.error.code || result.error.status
        if (code === 'over_email_send_rate_limit' || code === 'too_many_requests' ||
            (result.error.message || '').toLowerCase().includes('rate')) {
          throw new Error('Príliš veľa pokusov. Skúste to znova o pár minút.')
        }
        console.warn('[Auth] resendVerification error:', result.error)
        throw new Error('Overovací e-mail sa nepodarilo odoslať. Skúste to neskôr alebo nás kontaktujte na podpora@edumisia.sk.')
      }
      return { success: true }
    } catch (err) {
      // Preserve friendly messages we already threw; wrap unknown errors
      if (err?.message && err.message.startsWith('Overovací') ||
          err?.message === 'Príliš veľa pokusov. Skúste to znova o pár minút.' ||
          err?.message?.startsWith('Zadajte')) {
        throw err
      }
      console.warn('[Auth] resendVerification unexpected error:', err?.message || err)
      throw new Error('Overovací e-mail sa nepodarilo odoslať. Skúste to neskôr alebo nás kontaktujte na podpora@edumisia.sk.')
    }
  }

  const signOut = async () => {
    console.log('[Auth] 🚪 Logout initiated')
    
    try {
      // CRITICAL: Always clear local state first, even if Supabase signOut fails
      // This ensures the UI becomes unusable immediately
      setUser(null)
      setSession(null)
      setAuthError(null)
      
      if (!supabase) {
        console.warn('[Auth] Supabase not initialized, but local state cleared')
        return { success: true, fallback: true }
      }

      // Try to sign out from Supabase
      // Use timeout to prevent hanging
      const signOutPromise = supabase.auth.signOut()
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Logout timeout')), 3000)
      )
      
      try {
        const result = await Promise.race([signOutPromise, timeoutPromise])
        
        if (result.error) {
          console.warn('[Auth] Supabase signOut error (local state already cleared):', result.error)
          // Don't throw - local state is already cleared
        } else {
          console.log('[Auth] ✅ Supabase signOut successful')
        }
      } catch (timeoutError) {
        console.warn('[Auth] Supabase signOut timeout (local state already cleared)')
        // Don't throw - local state is already cleared
      }
      
      // Extra safety: force cleanup localStorage
      try {
        await emergencyAuthCleanup()
      } catch (e) {
        console.warn('[Auth] Emergency cleanup warning:', e)
      }

      return { success: true }
      
    } catch (error) {
      console.error('[Auth] ❌ Logout error:', error)
      
      // Even if everything failed, force emergency cleanup
      try {
        await emergencyAuthCleanup()
      } catch (e) {
        console.error('[Auth] Final emergency cleanup failed:', e)
      }
      
      // Always return success because local state is cleared
      // The user can navigate away even if Supabase failed
      return { success: true, fallback: true }
    }
  }

  // Force logout - guaranteed to work even in broken state
  const forceLogout = async () => {
    console.warn('[Auth] 🚨 FORCE LOGOUT')
    await emergencyAuthCleanup()
    setUser(null)
    setSession(null)
    setAuthError(null)
    return { success: true, forced: true }
  }

  const resetPasswordForEmail = async (email) => {
    try {
      if (!supabase) {
        throw new Error('Supabase nie je inicializovaný')
      }

      // Always use the current origin for the reset link so the redirect
      // matches whatever environment the user is on (preview, production,
      // custom domain). No fallbacks, no env vars.
      const redirectUrl = `${window.location.origin}/reset-password`

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl
      })

      if (error) {
        throw new Error('Nepodarilo sa odoslať email. Skontrolujte emailovú adresu.')
      }

      return { success: true }
    } catch (error) {
      throw new Error(error.message || 'Nepodarilo sa odoslať email na reset hesla.')
    }
  }

  const updatePassword = async (newPassword) => {
    try {
      if (!supabase) {
        throw new Error('Supabase nie je inicializovaný')
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (error) {
        throw new Error('Nepodarilo sa zmeniť heslo. Skúste to prosím znova.')
      }

      return { success: true }
    } catch (error) {
      throw new Error(error.message || 'Nepodarilo sa zmeniť heslo.')
    }
  }


  // Refresh access - triggers Dashboard to reload data
  const refreshAccess = async () => {
    console.log('[Auth] 🔄 Refresh access triggered - setting payment success flag')
    // Set localStorage flag that Dashboard checks
    localStorage.setItem('payment_success_trigger', 'true')
    // Also fetch fresh access state
    await fetchPaidAccessState()
    return { success: true }
  }
  
  // SEPARATED ACCESS: Fetch comprehensive access status (test + school FG)
  const fetchPaidAccessState = async () => {
    if (!user) {
      setHasTestAccess(false)
      setHasSchoolFGAccess(false)
      setSchoolInfo(null)
      return
    }
    
    try {
      setAccessLoading(true)
      console.log('[Auth] 📡 Fetching comprehensive access status')
      
      const { data: sessionData } = await supabase.auth.getSession()
      const accessToken = sessionData?.session?.access_token
      
      if (!accessToken) {
        console.warn('[Auth] No access token, setting all access = false')
        setHasTestAccess(false)
        setHasSchoolFGAccess(false)
        setSchoolInfo(null)
        return
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/access-status`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        
        const hasTest = Boolean(data.access?.test_program)
        const hasSchoolFG = Boolean(data.access?.school_financial_literacy)
        
        console.log('[Auth] ✅ Access status:', {
          test_program: hasTest,
          school_financial_literacy: hasSchoolFG,
          school: data.school_info?.school_name || 'N/A'
        })
        
        setHasTestAccess(hasTest)
        setHasSchoolFGAccess(hasSchoolFG)
        setSchoolInfo(data.school_info)
      } else {
        console.error('[Auth] Failed to fetch access:', response.status)
        setHasTestAccess(false)
        setHasSchoolFGAccess(false)
        setSchoolInfo(null)
      }
    } catch (error) {
      console.error('[Auth] Error fetching access:', error)
      setHasTestAccess(false)
      setHasSchoolFGAccess(false)
      setSchoolInfo(null)
    } finally {
      setAccessLoading(false)
    }
  }
  
  // Fetch admin role from user_profiles
  const fetchAdminRole = async () => {
    
    if (!user) {
      setIsAdmin(false)
      setIsOwner(false)
      setAdminChecked(true)
      return
    }
    
    try {
      console.log('[Auth] 🔐 Checking admin role for user ID:', user.id)
      const { data, error } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', user.id)
        .single()
      
      if (error) {
        console.error('[Auth] Admin check error:', error.message)
        setIsAdmin(false)
        setIsOwner(false)
        setAdminChecked(true)
      } else {
        const adminStatus = data?.role === 'admin'
        console.log('[Auth] Admin status for', user.id, ':', adminStatus, '| role=', data?.role)
        setIsAdmin(adminStatus)

        // Phase 3: query backend for owner flag (env-controlled).
        // Only meaningful for admins; for non-admins always false.
        if (adminStatus) {
          try {
            const { data: sessionData } = await supabase.auth.getSession()
            const token = sessionData?.session?.access_token
            if (token) {
              const resp = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/me/role-info`,
                { headers: { Authorization: `Bearer ${token}` } },
              )
              if (resp.ok) {
                const info = await resp.json()
                setIsOwner(Boolean(info.is_owner))
                console.log('[Auth] 👑 Owner flag from backend:', info.is_owner)
              } else {
                setIsOwner(false)
              }
            } else {
              setIsOwner(false)
            }
          } catch (ownerErr) {
            console.error('[Auth] Owner-flag fetch failed (non-fatal):', ownerErr)
            setIsOwner(false)
          }
        } else {
          setIsOwner(false)
        }

        setAdminChecked(true)
        console.log('[Auth] ✅ Admin check complete:', adminStatus)
      }
    } catch (error) {
      console.error('[Auth] Admin fetch error:', error)
      setIsAdmin(false)
      setIsOwner(false)
      setAdminChecked(true)
    }
  }

  // Fetch access status whenever user changes
  useEffect(() => {
    if (user) {
      fetchPaidAccessState()
      
      fetchAdminRole()
    } else {
      setHasTestAccess(false)
      setHasSchoolFGAccess(false)
      setSchoolInfo(null)
      setIsAdmin(false)
      setIsOwner(false)
      setAdminChecked(true)
    }
  }, [user])

  // Context value with only primitives
  const value = {
    user,
    session,
    loading,
    authError,
    hasPaidAccess,        // LEGACY: Maps to hasTestAccess for backward compatibility
    hasTestAccess,        // SEPARATED: Regular test program access
    hasSchoolFGAccess,    // SEPARATED: School Financial Literacy access
    schoolInfo,           // School metadata (if user is school-linked)
    accessLoading,
    isAdmin,              // Admin role state
    adminChecked,
    isOwner,              // Phase 3 — owner / super-admin flag (env-controlled)
    signUp,
    signIn,
    resendVerification,
    signOut,
    forceLogout,
    emergencyAuthCleanup,
    refreshAccess,
    fetchPaidAccessState,
    fetchAdminRole,
    resetPasswordForEmail,
    updatePassword,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
